<template>
  <div
    class="payment-method-card mb-3 rounded-6 overflow-hidden cursor-pointer delay"
    :class="isSelected ? 'border-green bg-light-page' : 'bg-white border-form'">
    <div class="d-flex" @click="$emit('select')">
      <div class="payment-method-card__logo-panel">
        <img :src="logoUrl" :alt="payment.name" class="payment-method-card__logo" />
        <span v-if="chargeInfo" class="payment-method-card__currency">{{ chargeInfo.currencyCode }}</span>
      </div>
      <div class="payment-method-card__body">
        <div class="payment-method-card__content">
          <p class="mb-0 font-bold payment-method-card__name">{{ payment.name }}</p>
          <span v-if="!isGroup" class="payment-method-card__status font-semibold" :class="statusClass">
            <Clock v-if="isManual" :size="12" />
            <CheckCircle v-else :size="12" />
            {{ statusLabel }}
          </span>
          <div
            v-if="payment.description"
            class="payment-method-card__description"
            :class="isSelected ? 'html-desc' : 'html-desc-truncate'"
            v-html="payment.description"></div>
          <PaymentChargeInfo v-if="isSelected && chargeInfo" :charge-info="chargeInfo" class="mt-2" />
          <p v-if="isSelected && hasCurrencyDisclaimer" class="payment-method-card__disclaimer">
            <Info :size="14" class="flex-shrink-0 mt-2px" />
            {{ $t('hotel.payment-method.idr-conversion-disclaimer') }}
          </p>
        </div>
        <ChevronDown v-if="isGroup" :size="18" class="payment-method-card__control" :class="{ 'is-open': isGroupOpen }" />
        <div v-else class="payment-method-card__control w-16px rounded-100 border-2-green">
          <div class="w-8px rounded-100 position-relative mx-auto mt-2px" :class="{ 'bg-green': isSelected }"></div>
        </div>
      </div>
    </div>
    <slot />
  </div>
</template>

<script>
import { ChevronDown, CheckCircle, Clock, Info } from 'lucide-vue';
import PaymentChargeInfo from './PaymentChargeInfo.vue';

export default {
  name: 'PaymentMethodCard',
  components: {
    ChevronDown,
    CheckCircle,
    Clock,
    Info,
    PaymentChargeInfo
  },
  props: {
    payment: {
      type: Object,
      default: () => ({})
    },
    imageUrl: {
      type: String,
      default: ''
    },
    isSelected: {
      type: Boolean,
      default: false
    },
    isGroup: {
      type: Boolean,
      default: false
    },
    isGroupOpen: {
      type: Boolean,
      default: false
    },
    chargeInfo: {
      type: Object,
      default: null
    },
    hasCurrencyDisclaimer: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    logoUrl() {
      return this.imageUrl || this.payment.image_url;
    },
    isManual() {
      return this.$isPaymentManual(this.payment);
    },
    statusLabel() {
      return this.$t(`general.${this.isManual ? 'manually-checked' : 'checked-automatically'}`);
    },
    statusClass() {
      return this.isManual ? 'is-manual' : 'is-automatic';
    }
  }
};
</script>

<style scoped>
.payment-method-card__logo-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  gap: 6px;
  width: 104px;
  padding: 12px 8px;
  /* translucent so the panel stays distinct on both the white and the selected card background */
  background-color: rgba(0, 0, 0, 0.03);
}

.payment-method-card__logo {
  width: 72px;
  height: 44px;
  object-fit: contain;
}

.payment-method-card__currency {
  padding: 1px 8px;
  border: 1px solid #dee2e6;
  border-radius: 999px;
  background-color: #fff;
  color: #222222;
  font-family: Nunito-Bold;
  font-size: 11px;
  letter-spacing: 0.5px;
  line-height: 1.5;
}

.payment-method-card__body {
  display: flex;
  flex: 1;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  padding: 12px 16px;
}

.payment-method-card__content {
  flex: 1;
  min-width: 0;
}

.payment-method-card__name {
  color: #222222;
  font-size: 18px;
  letter-spacing: 0.2px;
  line-height: 1.3;
}

/* fixed semantic colors: merchant theme colors can't guarantee the success/warning meaning */
.payment-method-card__status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  letter-spacing: 0.2px;
  line-height: 1.5;
}

.payment-method-card__status.is-automatic {
  background-color: #e8f5ee;
  color: #1a7f4b;
}

.payment-method-card__status.is-manual {
  background-color: #fff4e5;
  color: #b25e00;
}

.payment-method-card__description {
  margin-top: 6px;
  color: #666666;
  font-family: Nunito-Regular;
  font-size: 14px;
  letter-spacing: 0.2px;
  line-height: 1.5;
}

.payment-method-card__disclaimer {
  display: flex;
  gap: 6px;
  margin: 8px 0 0;
  padding: 6px 8px;
  border-radius: 6px;
  background-color: rgba(0, 0, 0, 0.03);
  color: #666666;
  font-family: Nunito-Regular;
  font-size: 12px;
  letter-spacing: 0.2px;
  line-height: 1.5;
}

/* centers the control on the first line of the name (18px × 1.3 line-height) */
.payment-method-card__control {
  flex-shrink: 0;
  margin-top: 4px;
  transition: transform 0.2s;
}

.payment-method-card__control.is-open {
  transform: rotate(180deg);
}

@media screen and (max-width: 768px) {
  .payment-method-card__logo-panel {
    width: 80px;
  }

  .payment-method-card__logo {
    width: 56px;
    height: 36px;
  }

  .payment-method-card__body {
    padding: 12px;
  }

  .payment-method-card__name {
    font-size: 16px;
  }

  .payment-method-card__control {
    margin-top: 2px;
  }

  .payment-method-card__description {
    font-size: 13px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/v2/payment/PaymentMethodCard.vue